/**
 * Persiste e reaplica fundo virtual da webcam.
 *
 * O RealtimeKit perde middlewares quando o track é recriado (constraints,
 * device change, enable/disable) ou quando o Chrome suspende o worker de
 * segmentação — inclusive com a pessoa no PC, só porque outra janela
 * (WhatsApp, e-mail) ficou na frente.
 */

import type RealtimeKitClient from "@cloudflare/realtimekit";
import RealtimeKitVideoBackgroundTransformer from "@cloudflare/realtimekit-virtual-background";
import { ensurePreferredCamera } from "./cameraFacing";

const STORAGE_KEY = "constela.virtualBackground.v1";
const IMAGE_IDB_NAME = "constela-vb-v1";
const IMAGE_IDB_STORE = "kv";
const IMAGE_IDB_KEY = "virtualBackgroundImage";
const IDB_POINTER = "idb:virtualBackgroundImage";
const REAPPLY_DEBOUNCE_MS = 400;
const MIN_REAPPLY_GAP_MS = 2500;
const JOIN_REAPPLY_DELAYS_MS = [200, 800, 1800];
const LOCAL_PREF_MAX_CHARS = 1_200_000;

/** Papel de parede da sala — URL estável (não blob). */
export const CONSTELA_CAMERA_BACKGROUND_URL = "/assets/fundo2.jpg";

export type VirtualBackgroundPreference =
  | { mode: "blur"; blurStrength: number }
  | { mode: "virtual"; imageUrl: string }
  | { mode: "none" };

type VideoMiddleware = Parameters<
  RealtimeKitClient["self"]["addVideoMiddleware"]
>[0];

type MeetingSelf = RealtimeKitClient["self"] & {
  addVideoMiddleware: (mw: VideoMiddleware) => unknown;
  removeVideoMiddleware?: (mw: VideoMiddleware) => unknown;
  removeAllVideoMiddlewares?: () => unknown;
  updateVideoConstraints?: (constraints: unknown) => unknown;
  on?: (event: string, handler: (...args: unknown[]) => void) => void;
  off?: (event: string, handler: (...args: unknown[]) => void) => void;
  removeListener?: (event: string, handler: (...args: unknown[]) => void) => void;
};

type StampedMiddleware = VideoMiddleware & {
  __vb?: VirtualBackgroundPreference;
};

let lastMiddleware: VideoMiddleware | null = null;
let transformerPromise: Promise<RealtimeKitVideoBackgroundTransformer> | null =
  null;
let transformerMeeting: RealtimeKitClient | null = null;
const patchedMeetings = new WeakSet<object>();
let reapplyTimer: number | null = null;
let reapplyInFlight = false;
let lastReapplyAt = 0;
let originalRemoveAllRef: (() => unknown) | null = null;

function storageGet(key: string): string | null {
  try {
    return localStorage.getItem(key) ?? sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function storageSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // ignore
  }
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // ignore
  }
}

function storageRemove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
  try {
    sessionStorage.removeItem(key);
  } catch {
    // ignore
  }
}

function openImageDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = window.indexedDB.open(IMAGE_IDB_NAME, 1);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(IMAGE_IDB_STORE)) {
        req.result.createObjectStore(IMAGE_IDB_STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idbSetImage(dataUrl: string): Promise<void> {
  const db = await openImageDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(IMAGE_IDB_STORE, "readwrite");
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.objectStore(IMAGE_IDB_STORE).put(dataUrl, IMAGE_IDB_KEY);
  });
}

async function idbGetImage(): Promise<string | null> {
  try {
    const db = await openImageDb();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(IMAGE_IDB_STORE, "readonly");
      const req = tx.objectStore(IMAGE_IDB_STORE).get(IMAGE_IDB_KEY);
      req.onsuccess = () =>
        resolve(typeof req.result === "string" ? req.result : null);
      req.onerror = () => reject(req.error);
    });
  } catch {
    return null;
  }
}

async function idbClearImage(): Promise<void> {
  try {
    const db = await openImageDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(IMAGE_IDB_STORE, "readwrite");
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.objectStore(IMAGE_IDB_STORE).delete(IMAGE_IDB_KEY);
    });
  } catch {
    // ignore
  }
}

function isDurableImageUrl(url: string): boolean {
  return /^(data:|https?:|\/)/i.test(url) && !url.startsWith("blob:");
}

async function blobToJpegDataUrl(blob: Blob): Promise<string> {
  const objectUrl = URL.createObjectURL(blob);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error("falha ao ler imagem"));
      el.src = objectUrl;
    });
    const max = 1280;
    let width = img.naturalWidth || img.width;
    let height = img.naturalHeight || img.height;
    if (width < 2 || height < 2) {
      throw new Error("imagem inválida");
    }
    if (Math.max(width, height) > max) {
      const scale = max / Math.max(width, height);
      width = Math.round(width * scale);
      height = Math.round(height * scale);
    }
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      throw new Error("canvas indisponível");
    }
    ctx.drawImage(img, 0, 0, width, height);
    return canvas.toDataURL("image/jpeg", 0.72);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

export async function toDurableBackgroundUrl(source: string | File): Promise<string> {
  if (typeof source === "string" && isDurableImageUrl(source) && !source.startsWith("data:")) {
    return source;
  }
  if (typeof source === "string" && source.startsWith("data:image/")) {
    return source;
  }
  const blob =
    source instanceof File
      ? source
      : await fetch(source).then((response) => {
          if (!response.ok) {
            throw new Error("não foi possível carregar a imagem");
          }
          return response.blob();
        });
  return blobToJpegDataUrl(blob);
}

function readPreference(): VirtualBackgroundPreference | null {
  try {
    const raw = storageGet(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as VirtualBackgroundPreference;
    if (!parsed || typeof parsed !== "object" || !("mode" in parsed)) {
      return null;
    }
    if (
      parsed.mode === "virtual" &&
      typeof parsed.imageUrl === "string" &&
      parsed.imageUrl.startsWith("blob:")
    ) {
      return lastMiddleware ? parsed : null;
    }
    return parsed;
  } catch {
    return null;
  }
}

async function resolvePreference(): Promise<VirtualBackgroundPreference | null> {
  const preference = readPreference();
  if (!preference) {
    return null;
  }
  if (preference.mode === "virtual" && preference.imageUrl === IDB_POINTER) {
    const stored = await idbGetImage();
    if (!stored) {
      return null;
    }
    return { mode: "virtual", imageUrl: stored };
  }
  return preference;
}

export async function persistVirtualBackgroundPreference(
  preference: VirtualBackgroundPreference,
): Promise<void> {
  if (preference.mode === "none") {
    saveVirtualBackgroundPreference(preference);
    await idbClearImage();
    return;
  }
  if (preference.mode === "blur") {
    saveVirtualBackgroundPreference(preference);
    return;
  }
  let imageUrl = preference.imageUrl;
  if (!isDurableImageUrl(imageUrl) || imageUrl.startsWith("blob:")) {
    imageUrl = await toDurableBackgroundUrl(imageUrl);
  }
  const durable: VirtualBackgroundPreference = { mode: "virtual", imageUrl };
  if (imageUrl.startsWith("data:") && imageUrl.length > LOCAL_PREF_MAX_CHARS) {
    await idbSetImage(imageUrl);
    storageSet(STORAGE_KEY, JSON.stringify({ mode: "virtual", imageUrl: IDB_POINTER }));
    return;
  }
  saveVirtualBackgroundPreference(durable);
}

export function saveVirtualBackgroundPreference(
  preference: VirtualBackgroundPreference,
): void {
  try {
    if (preference.mode === "none") {
      storageRemove(STORAGE_KEY);
      lastMiddleware = null;
      return;
    }
    storageSet(STORAGE_KEY, JSON.stringify(preference));
  } catch {
    // storage indisponível — ignora
  }
}

export function loadVirtualBackgroundPreference(): VirtualBackgroundPreference | null {
  return readPreference();
}

function isUsableBackgroundUrl(value: string): boolean {
  return /^(https?:|blob:|data:)/i.test(value);
}

function findInObject(
  obj: unknown,
  pick: (key: string, value: unknown) => string | number | null,
  depth = 0,
): string | number | null {
  if (!obj || typeof obj !== "object" || depth > 3) {
    return null;
  }
  const rec = obj as Record<string, unknown>;
  for (const [key, value] of Object.entries(rec)) {
    const hit = pick(key, value);
    if (hit != null) {
      return hit;
    }
  }
  for (const value of Object.values(rec)) {
    if (value && typeof value === "object") {
      const nested = findInObject(value, pick, depth + 1);
      if (nested != null) {
        return nested;
      }
    }
  }
  return null;
}

function inferPreferenceFromMiddleware(
  mw: VideoMiddleware,
): VirtualBackgroundPreference | null {
  if (mw == null) return null;

  const blur = findInObject(mw, (key, value) => {
    if (!/blur|strength|radius/i.test(key)) return null;
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? n : null;
  });
  if (typeof blur === "number") {
    return { mode: "blur", blurStrength: blur };
  }

  const url = findInObject(mw, (key, value) => {
    if (!/image|background|url|src/i.test(key)) return null;
    return typeof value === "string" && isUsableBackgroundUrl(value) ? value : null;
  });
  if (typeof url === "string") {
    return { mode: "virtual", imageUrl: url };
  }

  return null;
}

/**
 * O recorte padrão (0,5–0,75) só mostra a pele por inteiro acima de 0,75.
 * Entre os dois números a câmera é misturada com a imagem de fundo, e o
 * logo aparece quadriculado no lugar do rosto. A faixa curta deixa o rosto
 * opaco assim que a pessoa é reconhecida. lightWrapping 0 impede a cor do
 * fundo de tingir a pele. O filtro bilateral esconde o quadriculado da
 * máscara de 256 px nas bordas.
 */
const PERSON_MASK_POST_PROCESSING = {
  smoothSegmentationMask: true,
  coverage: [0.18, 0.28] as [number, number],
  lightWrapping: 0,
  jointBilateralFilter: { sigmaSpace: 8, sigmaColor: 0.2 },
};

/**
 * Modelo de paisagem do Google Meet (segm_full, 256×144). O padrão da
 * biblioteca é o ML Kit quadrado, treinado para selfie de celular: na webcam
 * ele trata o rosto como fundo e o logo vaza em blocos. 256×144 é a variante
 * que a biblioteca associa ao modelo completo do Meet.
 */
const MEET_SEGMENTATION_CONFIG = {
  model: "meet" as const,
  inputResolution: "256x144" as const,
  pipeline: "webgl2" as const,
  targetFps: 30,
};

async function getTransformer(
  meeting: RealtimeKitClient,
): Promise<RealtimeKitVideoBackgroundTransformer | null> {
  if (!RealtimeKitVideoBackgroundTransformer.isSupported()) {
    return null;
  }
  if (transformerMeeting !== meeting) {
    transformerPromise = null;
    transformerMeeting = meeting;
  }
  if (!transformerPromise) {
    transformerPromise = RealtimeKitVideoBackgroundTransformer.init({
      meeting,
      segmentationConfig: MEET_SEGMENTATION_CONFIG,
      postProcessingConfig: PERSON_MASK_POST_PROCESSING,
    }).catch((err) => {
      transformerPromise = null;
      transformerMeeting = null;
      throw err;
    });
  }
  try {
    return await transformerPromise;
  } catch (err) {
    console.warn("Falha ao iniciar VideoBackgroundTransformer:", err);
    return null;
  }
}

function resetTransformer(): void {
  transformerPromise = null;
  transformerMeeting = null;
}

/**
 * Intercepta add/remove de middlewares para lembrar o efeito ativo na sessão.
 */
export function installVirtualBackgroundCapture(
  meeting: RealtimeKitClient,
): () => void {
  const self = meeting?.self as MeetingSelf | undefined;
  if (!self || patchedMeetings.has(self as object)) {
    return () => undefined;
  }

  patchedMeetings.add(self as object);

  const originalAdd = self.addVideoMiddleware.bind(self);
  const originalRemove = self.removeVideoMiddleware?.bind(self);
  const originalRemoveAll = self.removeAllVideoMiddlewares?.bind(self);

  self.addVideoMiddleware = (mw: VideoMiddleware) => {
    lastMiddleware = mw;
    const stamped = (mw as StampedMiddleware).__vb;
    const inferred = stamped || inferPreferenceFromMiddleware(mw);
    if (inferred) {
      void persistVirtualBackgroundPreference(inferred);
    }
    return originalAdd(mw);
  };

  if (originalRemove) {
    self.removeVideoMiddleware = (mw: VideoMiddleware) => {
      return originalRemove(mw);
    };
  }

  originalRemoveAllRef = originalRemoveAll ?? null;

  if (originalRemoveAll) {
    self.removeAllVideoMiddlewares = () => {
      const result = originalRemoveAll();
      if (hadVirtualBackgroundInSession()) {
        scheduleReapplyVirtualBackground(meeting, true);
      }
      return result;
    };
  }

  const maybeReapply = (force = false) => {
    if (!self.videoEnabled) return;
    if (!hadVirtualBackgroundInSession()) return;
    scheduleReapplyVirtualBackground(meeting, force);
  };

  const onVideoUpdate = () => {
    window.setTimeout(() => maybeReapply(false), 300);
  };

  const onVisibleOrFocus = () => {
    if (document.visibilityState !== "visible") return;
    maybeReapply(true);
  };

  const onRoomJoined = () => {
    window.setTimeout(() => maybeReapply(true), 250);
  };

  self.on?.("videoUpdate", onVideoUpdate);
  self.on?.("deviceUpdate", onVideoUpdate);
  self.on?.("roomJoined", onRoomJoined);
  document.addEventListener("visibilitychange", onVisibleOrFocus);
  window.addEventListener("focus", onVisibleOrFocus);
  window.addEventListener("pageshow", onVisibleOrFocus);

  return () => {
    self.addVideoMiddleware = originalAdd;
    if (originalRemove) {
      self.removeVideoMiddleware = originalRemove;
    }
    if (originalRemoveAll) {
      self.removeAllVideoMiddlewares = originalRemoveAll;
    }
    self.off?.("videoUpdate", onVideoUpdate);
    self.removeListener?.("videoUpdate", onVideoUpdate);
    self.off?.("deviceUpdate", onVideoUpdate);
    self.removeListener?.("deviceUpdate", onVideoUpdate);
    self.off?.("roomJoined", onRoomJoined);
    self.removeListener?.("roomJoined", onRoomJoined);
    document.removeEventListener("visibilitychange", onVisibleOrFocus);
    window.removeEventListener("focus", onVisibleOrFocus);
    window.removeEventListener("pageshow", onVisibleOrFocus);
    originalRemoveAllRef = null;
    patchedMeetings.delete(self as object);
  };
}

export type ReapplyVirtualBackgroundResult =
  | "reapplied"
  | "none"
  | "unsupported"
  | "failed";

async function addMiddleware(
  self: MeetingSelf,
  mw: VideoMiddleware,
  meta?: VirtualBackgroundPreference,
): Promise<void> {
  if (meta) {
    (mw as StampedMiddleware).__vb = meta;
  }
  lastMiddleware = mw;
  self.addVideoMiddleware(mw);
}

/**
 * Reaplica o último fundo conhecido (middleware em memória ou preferência salva).
 */
export async function reapplyVirtualBackground(
  meeting: RealtimeKitClient,
): Promise<ReapplyVirtualBackgroundResult> {
  const self = meeting?.self as MeetingSelf | undefined;
  if (!self) {
    return "none";
  }
  if (reapplyInFlight) {
    return "none";
  }

  const existing = lastMiddleware;
  const preference = await resolvePreference();

  if (!existing && (!preference || preference.mode === "none")) {
    return "none";
  }

  reapplyInFlight = true;
  try {
    try {
      if (originalRemoveAllRef) {
        originalRemoveAllRef();
      } else {
        self.removeAllVideoMiddlewares?.();
      }
    } catch {
      // ignore
    }

    if (preference && preference.mode !== "none") {
      if (!RealtimeKitVideoBackgroundTransformer.isSupported()) {
        if (existing) {
          await addMiddleware(self, existing, preference);
          return "reapplied";
        }
        return "unsupported";
      }

      const transformer = await getTransformer(meeting);
      if (!transformer) {
        if (existing) {
          await addMiddleware(self, existing, preference);
          return "reapplied";
        }
        return "failed";
      }

      const mw =
        preference.mode === "blur"
          ? await transformer.createBackgroundBlurVideoMiddleware(
              preference.blurStrength,
            )
          : await transformer.createStaticBackgroundVideoMiddleware(
              preference.imageUrl,
            );
      await addMiddleware(self, mw, preference);
      lastReapplyAt = Date.now();
      return "reapplied";
    }

    if (existing) {
      await addMiddleware(self, existing);
      lastReapplyAt = Date.now();
      return "reapplied";
    }

    return "none";
  } catch (err) {
    console.warn("Falha ao reaplicar fundo virtual:", err);
    resetTransformer();
    if (existing) {
      try {
        await addMiddleware(self, existing);
        lastReapplyAt = Date.now();
        return "reapplied";
      } catch {
        return "failed";
      }
    }
    return "failed";
  } finally {
    reapplyInFlight = false;
  }
}

export function scheduleReapplyVirtualBackground(
  meeting: RealtimeKitClient,
  force = false,
): void {
  if (reapplyTimer != null) {
    window.clearTimeout(reapplyTimer);
  }
  reapplyTimer = window.setTimeout(() => {
    reapplyTimer = null;
    if (reapplyInFlight) return;
    if (!force && Date.now() - lastReapplyAt < MIN_REAPPLY_GAP_MS) return;
    if (!meeting?.self?.videoEnabled) return;
    if (!hadVirtualBackgroundInSession()) return;
    void reapplyVirtualBackground(meeting);
  }, REAPPLY_DEBOUNCE_MS);
}

export function updateVideoConstraintsPreservingBackground(
  meeting: RealtimeKitClient,
  constraints: unknown,
): void {
  const self = meeting?.self as MeetingSelf | undefined;
  if (!self?.updateVideoConstraints) {
    return;
  }
  // Não pôr facingMode aqui: vira applyConstraints na trilha atual, que não troca
  // de lente e brigava com a câmera traseira escolhida pela pessoa.
  try {
    void Promise.resolve(self.updateVideoConstraints(constraints)).finally(() => {
      scheduleReapplyVirtualBackground(meeting, true);
    });
  } catch (err) {
    console.warn("Falha ao atualizar constraints de vídeo:", err);
    scheduleReapplyVirtualBackground(meeting, true);
  }
}

/**
 * Liga o vídeo e tenta restaurar o fundo virtual em seguida.
 * @returns true se vale mostrar o toast pedindo para reativar o fundo manualmente
 */
export async function enableVideoAndRestoreBackground(
  meeting: RealtimeKitClient,
): Promise<{ enabled: boolean; suggestManualRestore: boolean }> {
  const self = meeting?.self;
  if (!self) {
    return { enabled: false, suggestManualRestore: false };
  }

  const hadEffectHint = hadVirtualBackgroundInSession();

  try {
    await Promise.resolve(self.enableVideo());
  } catch {
    return { enabled: false, suggestManualRestore: false };
  }

  await ensurePreferredCamera(meeting);

  // Pequeno atraso para o track estabilizar antes do middleware.
  await new Promise((r) => window.setTimeout(r, 250));

  const result = await reapplyVirtualBackground(meeting);
  if (result === "reapplied") {
    return { enabled: true, suggestManualRestore: false };
  }

  return {
    enabled: true,
    suggestManualRestore: hadEffectHint || result === "unsupported",
  };
}

export function hadVirtualBackgroundInSession(): boolean {
  if (lastMiddleware != null) return true;
  const pref = readPreference();
  return !!pref && pref.mode !== "none";
}

/**
 * Depois do join a câmera é recriada e o efeito da tela de configuração some.
 * Reaplica em alguns instantes para pegar o track publicado.
 */
export function restoreVirtualBackgroundAfterJoin(
  meeting: RealtimeKitClient,
): void {
  installVirtualBackgroundCapture(meeting);
  if (!hadVirtualBackgroundInSession()) {
    return;
  }
  for (const delay of JOIN_REAPPLY_DELAYS_MS) {
    window.setTimeout(() => {
      if (!meeting?.self?.videoEnabled) return;
      if (!hadVirtualBackgroundInSession()) return;
      void reapplyVirtualBackground(meeting);
    }, delay);
  }
}

/**
 * Aplica fundo a partir do controle da Constela (desfoque, papel ou upload).
 * Converte blob/arquivo para JPEG durável antes de gravar.
 */
export async function applyCameraBackground(
  meeting: RealtimeKitClient,
  preference: VirtualBackgroundPreference,
): Promise<ReapplyVirtualBackgroundResult> {
  installVirtualBackgroundCapture(meeting);
  const self = meeting?.self as MeetingSelf | undefined;
  if (!self) {
    return "none";
  }

  if (preference.mode === "none") {
    await persistVirtualBackgroundPreference(preference);
    lastMiddleware = null;
    try {
      if (originalRemoveAllRef) {
        originalRemoveAllRef();
      } else {
        self.removeAllVideoMiddlewares?.();
      }
    } catch {
      // ignore
    }
    return "none";
  }

  if (preference.mode === "virtual") {
    const imageUrl = await toDurableBackgroundUrl(preference.imageUrl);
    await persistVirtualBackgroundPreference({ mode: "virtual", imageUrl });
  } else {
    await persistVirtualBackgroundPreference(preference);
  }

  return reapplyVirtualBackground(meeting);
}

export async function applyConstelaCameraBackground(
  meeting: RealtimeKitClient,
): Promise<ReapplyVirtualBackgroundResult> {
  return applyCameraBackground(meeting, {
    mode: "virtual",
    imageUrl: CONSTELA_CAMERA_BACKGROUND_URL,
  });
}

export async function applyBlurCameraBackground(
  meeting: RealtimeKitClient,
  blurStrength = 18,
): Promise<ReapplyVirtualBackgroundResult> {
  return applyCameraBackground(meeting, { mode: "blur", blurStrength });
}

export async function applyUploadedCameraBackground(
  meeting: RealtimeKitClient,
  file: File,
): Promise<ReapplyVirtualBackgroundResult> {
  const imageUrl = await toDurableBackgroundUrl(file);
  return applyCameraBackground(meeting, { mode: "virtual", imageUrl });
}
